import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportEntity,
  ExportEntityType,
  ProductEntityRole,
} from 'api/ExportHubAPI';
import {
  PRODUCT_ENTITY_ROLE_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  RiskFlags,
} from './entityPresets';
import CreatableSelect from './CreatableSelect';
import { COUNTRIES } from './licensePresets';

interface Props {
  open: boolean;
  productId: string;
  groupId: string | undefined;
  linkedIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

const LinkEntityModal: React.FC<Props> = ({
  open,
  productId,
  groupId,
  linkedIds,
  onClose,
  onLinked,
}) => {
  const [search, setSearch] = useState('');
  const [entities, setEntities] = useState<ExportEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [role, setRole] = useState<ProductEntityRole>('Customer');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline "create new entity" form state.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<ExportEntityType>('Customer');
  const [newCountry, setNewCountry] = useState('');
  const [creatingSaving, setCreatingSaving] = useState(false);

  const load = useCallback(() => {
    if (!groupId) return;
    setLoading(true);
    ExportHubAPI.getEntities(groupId)
      .then((data) => setEntities(data))
      .catch(() => setEntities([]))
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    if (!open || !groupId) return;
    setSearch('');
    setSelectedId(null);
    setNotes('');
    setCreating(false);
    setNewName('');
    setNewType('Customer');
    setNewCountry('');
    load();
  }, [open, groupId, load]);

  const handleCreateEntity = async () => {
    const name = newName.trim();
    if (!name) {
      enqueueSnackbar('Enter an entity name.', { variant: 'warning' });
      return;
    }
    if (!groupId) return;
    setCreatingSaving(true);
    try {
      const created = await ExportHubAPI.createEntity({
        group_id: groupId,
        name: name,
        entity_type: newType,
        country: newCountry.trim() || undefined,
      });
      enqueueSnackbar(`Added “${created.name}” to your entity base.`, {
        variant: 'success',
      });
      // Refresh the catalog, then pre-select the new entity for linking.
      const data = await ExportHubAPI.getEntities(groupId);
      setEntities(data);
      setSelectedId(created.id);
      setRole(created.entity_type);
      setCreating(false);
      setNewName('');
      setNewCountry('');
    } catch (err) {
      console.error('Failed to create entity:', err);
      enqueueSnackbar('Failed to create entity.', { variant: 'error' });
    } finally {
      setCreatingSaving(false);
    }
  };

  const filtered = entities.filter((e) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      (e.country || '').toLowerCase().includes(q) ||
      e.entity_type.toLowerCase().includes(q)
    );
  });

  const select = (e: ExportEntity) => {
    setSelectedId(e.id);
    setRole(e.entity_type);
  };

  const handleLink = async () => {
    if (!groupId || !selectedId) return;
    setSaving(true);
    try {
      await ExportHubAPI.linkEntity(productId, {
        group_id: groupId,
        entity_id: selectedId,
        role: role,
        notes: notes.trim() || undefined,
      });
      enqueueSnackbar('Entity linked.', { variant: 'success' });
      onLinked();
    } catch (err) {
      console.error('Failed to link entity:', err);
      enqueueSnackbar('Failed to link entity.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const linkedSet = new Set(linkedIds);

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Link Entity'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Link Customer / Consignee</h2>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <div className='eh-modal-search'>
            <SearchIcon style={{ fontSize: 18 }} className='eh-modal-search-icon' />
            <input
              className='eh-input'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search entities by name, country, or type'
              autoFocus
            />
          </div>

          {loading && <div className='eh-detail-note'>Loading entities…</div>}
          {!loading && filtered.length === 0 && (
            <div className='eh-detail-note'>No matching entities.</div>
          )}

          <div className='eh-link-list'>
            {filtered.map((e) => {
              const already = linkedSet.has(e.id);
              return (
                <button
                  key={e.id}
                  type='button'
                  className={`eh-link-row ${selectedId === e.id ? 'sel' : ''} ${
                    already ? 'disabled' : ''
                  }`}
                  onClick={() => !already && select(e)}
                  disabled={already}
                >
                  <span
                    className={`eh-facet-check ${
                      already || selectedId === e.id ? 'on' : ''
                    }`}
                  >
                    {(already || selectedId === e.id) && '✓'}
                  </span>
                  <span className='eh-link-main'>
                    <span className='eh-link-title'>
                      {e.name}
                      <span className='eh-link-sub'>{e.entity_type}</span>
                    </span>
                    <span className='eh-link-meta'>
                      {e.country || '—'}
                      <RiskFlags flags={e.risk_flags} />
                    </span>
                  </span>
                  {already && <span className='eh-link-tag'>Linked</span>}
                </button>
              );
            })}
          </div>

          {/* Create a new entity to grow the team's entity base (spec §4.7). */}
          {creating ? (
            <div className='eh-entity-create-form'>
              <span className='eh-create-title'>New entity</span>
              <div className='eh-field-row'>
                <label className='eh-field'>
                  <span className='eh-field-label'>
                    Name <span className='eh-req'>*</span>
                  </span>
                  <input
                    className='eh-input'
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder='e.g. Acme Defence GmbH'
                    autoFocus
                  />
                </label>
                <div className='eh-field'>
                  <span className='eh-field-label'>Type</span>
                  <CreatableSelect
                    value={newType}
                    onChange={(v) => v && setNewType(v as ExportEntityType)}
                    options={ENTITY_TYPE_OPTIONS as unknown as string[]}
                    allowCreate={false}
                    disableClearable
                    placeholder='Type'
                    ariaLabel='Entity type'
                  />
                </div>
              </div>
              <div className='eh-field'>
                <span className='eh-field-label'>Country</span>
                <CreatableSelect
                  value={newCountry}
                  onChange={(v) => setNewCountry(v)}
                  options={COUNTRIES}
                  field='country'
                  groupId={groupId}
                  placeholder='Select or type a country'
                  ariaLabel='Entity country'
                />
              </div>
              <div className='eh-entity-create-row'>
                <button
                  className='btn-outline'
                  type='button'
                  onClick={() => setCreating(false)}
                  disabled={creatingSaving}
                >
                  Cancel
                </button>
                <button
                  className='btn-primary'
                  type='button'
                  onClick={handleCreateEntity}
                  disabled={creatingSaving || !newName.trim()}
                  style={{
                    opacity: creatingSaving || !newName.trim() ? 0.6 : 1,
                  }}
                >
                  {creatingSaving ? 'Adding…' : 'Add entity'}
                </button>
              </div>
            </div>
          ) : (
            <div className='eh-entity-create-row'>
              <span className='eh-detail-note'>
                Can&rsquo;t find it? Add it to your entity base.
              </span>
              <button
                className='btn-outline'
                type='button'
                onClick={() => {
                  setNewName(search.trim());
                  setCreating(true);
                }}
              >
                <AddIcon style={{ fontSize: 18 }} /> Create entity
              </button>
            </div>
          )}

          {selectedId && (
            <div className='eh-field-row'>
              <div className='eh-field'>
                <span className='eh-field-label'>Role on this product</span>
                <CreatableSelect
                  value={role}
                  onChange={(v) => v && setRole(v as ProductEntityRole)}
                  options={PRODUCT_ENTITY_ROLE_OPTIONS as unknown as string[]}
                  allowCreate={false}
                  disableClearable
                  placeholder='Role'
                  ariaLabel='Role on this product'
                />
              </div>
              <label className='eh-field'>
                <span className='eh-field-label'>Notes (optional)</span>
                <input
                  className='eh-input'
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={1000}
                  placeholder='e.g. primary purchaser'
                />
              </label>
            </div>
          )}
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={handleLink}
            disabled={saving || !selectedId}
            style={{ opacity: saving || !selectedId ? 0.6 : 1 }}
          >
            {saving ? 'Linking…' : 'Link Entity'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkEntityModal;
